// Third party
import React, { useEffect } from 'react';
import * as ProposalNS from '../../Proposal';

// Utils and helpers
import { DropItemPosition } from '@/utils/drag-and-drop';
import { getTabElements, getMaxNumberOfItems } from '@/modules/proposal/ProposalHelpers';

// Constants and types
import { ProposalSidebarOptions } from '../proposal-sidebar/ProposalSidebar';
import { PROPOSAL_ORIENTATION } from '../../utils/constants';
import User from '@/types/user';
import Locale from '@/types/locale';
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';

// Components
import EditProposalAdvisorInfo from '../proposal-sidebar/components/ProposalAdvisoryInfoEditor';
import ProposalColorSelector from '../proposal-sidebar/components/ProposalColorSelector';
import ProposalItemSelector from '../proposal-sidebar/components/ProposalItemSelector';
import ProposalLanguageSelector from '../proposal-sidebar/components/ProposalLanguageSelector';
import ProposalOrientationSelector from '../proposal-sidebar/components/ProposalOrientationSelector';
import ProposalPaperFormatSelector from '../proposal-sidebar/components/ProposalPaperFormatSelector';
import ProposalPatternSelector from '../proposal-sidebar/components/ProposalPatternSelector';
import ProposalTemplateButton from '../proposal-sidebar/components/ProposalTemplateSelector';
import ProposalItemLister from '../proposal-sidebar/components/proposal-item-lister/ProposalItemLister';

// Partials
import ContentPanel from './ContentPanel';
import NavigationHeader from './NavigationHeader';
import ProposalCoBrandingColors from '../proposal-sidebar/components/ProposalCoBrandingColors';
import AdditionalContentCollapsible from './additional-content/AdditionalContentCollapsible';
import { CollapsibleContainer } from '@/views/components/misc';
import { formatMessage } from '@/utils/i18n';
import { useProposalActions } from '../preview/ProposalActionsContext';

type Props = {
  user: User;
  locale: Locale;
  proposal: ProposalNS.Proposal;
  comparison: Comparison;
  onJumpToPage: (section: string) => void;
  onToggleHiddenLayout: (layout: string, selected: boolean) => void;
  onToggleSection: (section: string, selected: boolean) => void;
  onToggleDivision: (division: string, selected: boolean) => void;
  onToggleGifBaseScenario: (value: boolean) => void;
  onItemsSelection: (items: ComparisonItem[]) => void;
  onItemPurposeFlagUpdate: (itemId: string, flags: string[]) => void;
  onGifItemsSelection: (items: BMOGIFItem[]) => void;
  onChangeTemplate: () => void;
  onProposalStyleOptionsChange: (styleOptions: ProposalNS.ProposalStyleOptionsV2) => void;
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions;
  templates: ProposalNS.BaseProposalTemplate[];
  gifHideBaseScenario?: boolean;
  onToggleShortHistory: (value: boolean) => void;
  onEditBrandingInfo: () => void;
  pageIndexes?: ProposalNS.ProposalSectionPageMapping;
  onChangeItemOrder: (itemId: string, referenceId: string, position?: DropItemPosition) => void;
  onChangeContentOrder: (itemId: string, referenceId: string, position?: DropItemPosition) => void;
  pageRefs?: Array<React.RefObject<HTMLDivElement>>;
  template: ProposalNS.ProposalTemplateV2;
  downloadPdf: () => void;
};

const ProposalToolbox = (props: Props) => {
  const [sidebarToDisplay, setSidebarToDisplay] = React.useState<ProposalNS.ProposalSidebarTab>();
  const [showAddContentButton, setShowAddContentButton] = React.useState<boolean>(false);
  const {
    onOrientationChange,
    onHeaderColorChange,
    onHeaderPatternChange,
    onCoBrandingColorChange,
    onPaperFormatChange,
    onLocaleChange,
    onToggleFundProfiles,
    onToggleGroupProfiles,
    onTogglePortfolioProfiles,
  } = useProposalActions();
  // Show the add content button or not
  useEffect(() => {
    props.template.sidebarTabs.length > 0 ? setShowAddContentButton(true) : setShowAddContentButton(false);
  }, [props.template.sidebarTabs.length]);

  const renderSidebarElement = (element: ProposalNS.ProposalSidebarElement, key: string) => {
    const { proposalTemplateOptions, proposal, comparison, user, templates, gifHideBaseScenario, locale } = props;
    const {
      items,
      template,
      template: { customParameters },
    } = proposal;
    const { allowedItemTypes } = template.itemManagement;
    const { itemSelectors } = element as ProposalNS.ProposalItemSelection;
    const {
      styleOptions: { orientation },
    } = proposalTemplateOptions;

    let renderedElement;
    const maxItems: number = getMaxNumberOfItems(template, orientation);
    switch (element.type) {
      case ProposalSidebarOptions.PROPOSAL_SECTIONS: {
        // Check if proposal has any items - if it's only GIF items, skip content selection.
        if (proposal.items.length === 0) {
          break;
        }

        renderedElement = (
          <ContentPanel
            user={user}
            proposalTemplateOptions={proposalTemplateOptions}
            proposal={proposal}
            onJumpToPage={props.onJumpToPage}
            onToggleSection={props.onToggleSection}
            onToggleDivision={props.onToggleDivision}
            onChangeItemOrder={props.onChangeItemOrder}
            onChangeContentOrder={props.onChangeContentOrder}
          />
        );
        break;
      }
      case ProposalSidebarOptions.ITEM_SELECTOR: {
        renderedElement = (
          <ProposalItemSelector
            onSelection={props.onItemsSelection}
            comparisonItems={comparison.items}
            selectedProposalItems={items}
            numberOfControls={maxItems}
            itemSelectors={itemSelectors}
            singleItemShift={customParameters && customParameters.singleItemShift}
            allowedItemTypes={allowedItemTypes}
          />
        );
        break;
      }
      case ProposalSidebarOptions.ITEM_LISTER: {
        renderedElement = (
          <CollapsibleContainer className="proposal-item-lister" headerText={formatMessage('Proposal.ItemSelection')} headerTag="h2">
            <ProposalItemLister
              user={user}
              locale={locale}
              onItemsSelection={props.onItemsSelection}
              onItemPurposeFlagUpdate={props.onItemPurposeFlagUpdate}
              onGifItemsSelection={props.onGifItemsSelection}
              onToggleGifBaseScenario={props.onToggleGifBaseScenario}
              onToggleFundProfiles={onToggleFundProfiles}
              onTogglePortfolioProfiles={onTogglePortfolioProfiles}
              onToggleGroupProfiles={onToggleGroupProfiles}
              onToggleShortHistory={props.onToggleShortHistory}
              proposal={proposal}
              comparison={comparison}
              allowedItemTypes={allowedItemTypes}
              maxItemsNumber={maxItems}
              gifHideBaseScenario={gifHideBaseScenario}
              showIncludeFundProfiles={element.showIncludeFundProfiles}
              showIncludePortfolioProfiles={element.showIncludePortfolioProfiles}
              showIncludeGroupProfiles={element.showIncludeGroupProfiles}
              showShortHistory={element.showShortHistory}
              showProposalItemPurposeSelector={element.showProposalItemPurposeSelector}
              itemColorOverride={element.itemColorOverride}
              onChangeItemOrder={props.onChangeItemOrder}
            />
          </CollapsibleContainer>
        );
        break;
      }
      case ProposalSidebarOptions.LANGUAGE: {
        const {
          appConfig: { appLocales },
        } = user;

        renderedElement = <ProposalLanguageSelector onLocaleChange={onLocaleChange} selectedLocale={proposal.options.locale} appLocales={appLocales} />;
        break;
      }
      case ProposalSidebarOptions.TEMPLATE: {
        if (templates.length > 1) {
          renderedElement = <ProposalTemplateButton onChangeTemplate={props.onChangeTemplate} />;
        }
        break;
      }
      case ProposalSidebarOptions.EDIT_PROPOSAL_ADVISOR_INFO: {
        renderedElement = <EditProposalAdvisorInfo onEditBrandingInfo={props.onEditBrandingInfo} />;
        break;
      }
      case ProposalSidebarOptions.PATTERN_SELECTOR: {
        renderedElement = <ProposalPatternSelector onHeaderPatternChange={onHeaderPatternChange} styleOptions={proposalTemplateOptions.styleOptions} />;
        break;
      }
      case ProposalSidebarOptions.COLOR_SELECTOR: {
        renderedElement = <ProposalColorSelector onHeaderColorChange={onHeaderColorChange} styleOptions={proposalTemplateOptions.styleOptions} />;
        break;
      }
      case ProposalSidebarOptions.COBRANDING_COLOR: {
        renderedElement = (
          <ProposalCoBrandingColors onUpdateCoBrandedColors={onCoBrandingColorChange} styleOptions={proposalTemplateOptions.styleOptions} user={user} />
        );
        break;
      }
      case ProposalSidebarOptions.ORIENTATION: {
        renderedElement = (
          <ProposalOrientationSelector
            onOrientationChange={onOrientationChange}
            selectedOrientation={proposalTemplateOptions.styleOptions.orientation || PROPOSAL_ORIENTATION.PORTRAIT}
          />
        );
        break;
      }
      case ProposalSidebarOptions.PAPER_FORMAT: {
        renderedElement = (
          <ProposalPaperFormatSelector onPaperFormatChange={onPaperFormatChange} selectedFormat={proposalTemplateOptions.styleOptions.paperFormat} />
        );
        break;
      }
    }

    return !renderedElement ? null : (
      <div className="proposal-sidebar-section" key={key}>
        {renderedElement}
      </div>
    );
  };

  const itemListerElement = getTabElements(sidebarToDisplay, props.user).find(
    (element: ProposalNS.ProposalSidebarElement) => element.type === ProposalSidebarOptions.ITEM_LISTER,
  );

  return (
    <div className="proposal-toolbox-container">
      <div className="white-space" />
      <aside className="proposal-toolbox">
        <NavigationHeader
          user={props.user}
          template={props.template}
          setSidebarToDisplay={setSidebarToDisplay}
          setShowAddContentButton={setShowAddContentButton}
          downloadPdf={props.downloadPdf}
        />
        <div className="proposal-toolbox-content-panel">
          {props.template.sidebarTabs.map(tab => {
            const isActive = sidebarToDisplay?.key === tab.key;
            return (
              <div
                id={`proposal-toolbox-${tab.key}-panel`}
                key={tab.key}
                role="tabpanel"
                hidden={!isActive}
                aria-hidden={!isActive}
                aria-labelledby={`proposal-toolbox-${tab.key}-trigger`}
                tabIndex={0}
              >
                {getTabElements(tab, props.user).map((element: ProposalNS.ProposalSidebarElement) => renderSidebarElement(element, `tab ${element.type}`))}
              </div>
            );
          })}
        </div>
        {showAddContentButton && (
          <AdditionalContentCollapsible
            user={props.user}
            pageIndexes={props.pageIndexes}
            proposal={props.proposal}
            onToggleHiddenLayout={props.onToggleHiddenLayout}
            onToggleSection={props.onToggleSection}
            onToggleDivision={props.onToggleDivision}
            proposalTemplateOptions={props.proposalTemplateOptions}
            hiddenLayouts={props.proposalTemplateOptions.hiddenLayouts}
            selectedSections={props.proposalTemplateOptions.sections}
            selectedDivisions={props.proposalTemplateOptions.divisions}
            pageRefs={props.pageRefs}
            locale={props.locale}
            onJumpToPage={props.onJumpToPage}
            itemListerElement={itemListerElement as ProposalNS.ProposalSidebarItemLister}
            showLoaders={props.comparison.items.reduce((acc, item) => acc + item.holdings.length, 0) > 100}
          />
        )}
      </aside>
    </div>
  );
};

export default ProposalToolbox;
